// components/auth/EmailVerificationStepTailwind.tsx

import { useState, useEffect } from 'react';
import { EmailRequest } from '@/_shared/_api/models/email.model';
import { Button } from '@/components/buttons';
import { AlertBox } from '@/components/alert-message';
import {
  Send,
  RefreshCw,
  Check,
  AlertTriangle,
  MailWarning,
} from 'lucide-react';
import { useRouter } from 'next/router';
import { useSendVerification } from '@/_shared/hooks/function/useEmailService.hooks';

interface Props {
  email: string;
  regStatus: RegAndEmailStatus;
  data: {
    firstName: string;
    lastName: string;
  };
  onBackToLogin?: () => void;
}

export type RegAndEmailStatus =
  | 'initial'
  | 'exist-not-verified'
  | 'exist-verified'
  | 'sending'
  | 'success'
  | 'error';

export default function EmailVerificationStep({
  email,
  data,
  regStatus,
  onBackToLogin,
}: Props) {
  const route = useRouter();
  const [status, setStatus] = useState<RegAndEmailStatus>(regStatus);

  useEffect(() => {
    setStatus(regStatus);
  }, [regStatus]);

  const { mutateAsync: sendVerificationEmail } = useSendVerification();

  const resendEmailVerification = async () => {
    try {
      setStatus('sending');

      const emailRequest: EmailRequest = {
        recepient: {
          email: email,
          name: data.firstName + ' ' + data.lastName,
        },
      };

      const emailSent = await sendVerificationEmail(emailRequest);

      if (emailSent.data) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const getAlertBox = () => {
    switch (status) {
      case 'sending':
        return (
          <AlertBox
            title="Sending verification email"
            message="Please wait while we send the verification link..."
            variant="info"
            icon={<RefreshCw className="animate-spin" />}
          />
        );
      case 'success':
        return (
          <AlertBox
            title="Email Sent Successfully"
            message="We've sent a new verification link to your email. Please check your inbox and spam folder."
            variant="success"
            icon={<Check />}
          />
        );
      case 'error':
        return (
          <AlertBox
            title="Failed to Send"
            message="We encountered an issue sending the verification email. Please try again."
            variant="error"
            icon={<AlertTriangle />}
          />
        );
      case 'exist-not-verified':
        return (
          <AlertBox
            title="Email is not verified!"
            message={
              <>
                We have sent a verification link to your email. <br />
                Please check your inbox and spam folder.
              </>
            }
            variant="info"
            icon={<MailWarning />}
          />
        );
      default:
        return (
          <AlertBox
            title="Request sent!"
            message={
              <>
                Please check your email for the instructions. <br />
                You might need to check your inbox and spam folder.
              </>
            }
            variant="success"
            icon={<Send />}
          />
        );
    }
  };

  return (
    <div className="w-full">
      <h1 className="text-3xl font-bold tracking-[0.15px] text-title mb-3">
        Please verify your email
      </h1>
      <p className="text-sm font-medium text-gray-500 mb-8">
        We sent a verification link to{' '}
        <span className="font-medium">{email}</span>
      </p>

      <div className="mb-6">{getAlertBox()}</div>

      <Button
        type="button"
        className="w-full mb-3"
        disabled={status === 'sending'}
        onClick={resendEmailVerification}
      >
        {status === 'sending' ? 'Sending...' : `Resend Verification Email`}
      </Button>

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={() => (onBackToLogin ? onBackToLogin() : route.push('/login'))}
      >
        Back to Login
      </Button>
    </div>
  );
}
